import { Navigate, useLocation } from "react-router-dom";
import { useAdminAuth, reloadAdminProfile, type AppRole } from "@/hooks/useAdminAuth";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";

interface Props {
  children: React.ReactNode;
  requireRole?: AppRole;
}

export const ProtectedRoute = ({ children, requireRole }: Props) => {
  const { loading, session, isStaff, roles, error } = useAdminAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="grid min-h-screen place-items-center bg-background">
        <Loader2 className="size-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!session || !isStaff) {
    if (session && error) {
      return (
        <div className="grid min-h-screen place-items-center bg-background p-8 text-center">
          <div className="max-w-md space-y-4">
            <h1 className="font-display text-2xl font-bold text-foreground">Vérification admin indisponible</h1>
            <p className="text-sm text-muted-foreground">
              La connexion est active, mais le backend n'a pas encore confirmé les droits.
            </p>
            <div className="flex justify-center gap-2">
              <Button onClick={() => reloadAdminProfile()}>Réessayer</Button>
              <Button
                variant="outline"
                onClick={async () => {
                  await supabase.auth.signOut();
                  try {
                    Object.keys(localStorage)
                      .filter((k) => k.startsWith("sb-") && k.endsWith("-auth-token"))
                      .forEach((k) => localStorage.removeItem(k));
                  } catch {
                    /* ignore */
                  }
                  window.location.href = "/admin/login";
                }}
              >
                Se déconnecter
              </Button>
            </div>
          </div>
        </div>
      );
    }
    return <Navigate to="/admin/login" replace state={{ from: location }} />;
  }

  if (requireRole && !roles.includes(requireRole) && !roles.includes("super_admin")) {
    return (
      <div className="grid min-h-screen place-items-center bg-background p-8 text-center">
        <div className="space-y-3">
          <h1 className="font-display text-2xl font-bold text-foreground">Accès refusé</h1>
          <p className="text-sm text-muted-foreground">
            Tu n'as pas les droits suffisants pour accéder à cette page.
          </p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};
